import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { BaseCard } from '@/components/uiKit/Cards';
import CardTitle from '@/components/uiKit/Cards/CardTitle';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import {
  CheckboxDropdown,
  CheckboxDropdownProps,
  type CheckboxOption,
} from '@/components/uiKit/Dropdown/CheckboxDropdown';
import { DataLayerDashboardContainer } from './DataLayerDashboardContainer';
import {
  DataLayerFilters,
  type DataLayerFilter,
} from '@/modules/AssetsStudioPage/SensorFusion/components/DataLayerFilters';
import { GroupDataLayerFilters } from '@/modules/AssetsStudioPage/SensorFusion/components/GroupDataLayerFilters';
import type { LegendSyncMode } from '@/modules/AssetsStudioPage/SensorFusion/hooks';
import { generateTestId } from '@/utils/shared/generateTestIds';
import { cn } from '@/lib/utils';
import { useGroupedSelection } from '@/components/uiKit/Dropdown/useGroupedSelection';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';

// Utility function to convert snake_case or kebab-case to Title Case
const toLabel = (id: string) => id.replace(/[_-]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

interface ManagedGraphsSectionProps {
  // Core props
  title: string;
  dataLayer: string;
  graphs: any[];
  selectedSections: string[];
  onSelectionChange: (selected: string[]) => void;
  syncSectionLegends?: boolean;

  // Reset functionality
  showReset?: boolean;
  triggerReset?: boolean;
  onReset?: () => void;
  onResetDataLayer?: () => void;
  loadingState?: boolean;

  // Query and data props
  queryParams: any;
  forceRefresh?: number;
  onLoadingStateChange?: (isLoading: boolean) => void;

  // Custom rendering
  includeAlarms?: boolean;
  variant?: 'lines' | 'columns' | 'integrated';

  // Optional custom title component
  customTitle?: React.ReactNode;

  // Additional header actions (e.g., filters)
  headerActions?: React.ReactNode;

  // Actions rendered below the chart area (e.g., playback controls for Waveform)
  chartFooterActions?: React.ReactNode;

  // Card customization
  cardClassName?: string;
  contentClassName?: string;

  // Shared vertical lines state
  verticalLinesState?: any;

  // Shared benchmark state (lifted to parent level to persist across component switches)
  benchmarkLineBySection?: Record<string, string | null>;
  setBenchmarkLineBySection?: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;

  // Control marking synchronization across sections
  syncMarking?: boolean; // If false, each section has independent marking tools

  // Data layer filters
  filters?: DataLayerFilter[];
  onFilterChange?: (filterId: string, value: string | string[]) => void;

  // External legend state management
  externalLegendStates?: Record<string, boolean>;
  onLegendToggle?: (itemId: string, enabled: boolean) => void;

  // Direct sensor toggle handler (for search filters to use same handlers as legends)
  onToggleSensor?: (sectionId: string, sensorName: string) => void;
  onAddSensor?: (sectionId: string, sensorName: string) => void;
  registerSensorToggleHandler?: (dataLayer: string, handler: (sectionId: string, sensorName: string) => void) => void;
  registerSensorData?: (
    dataLayer: string,
    enabledSensors: Record<string, boolean>,
    sensorColors: Record<string, string>
  ) => void;

  // Sensor data for search filters (same as comparison table)
  sectionEnabledSensors?: Record<string, Record<string, boolean>>; // All sections' sensors
  sectionSensorColors?: Record<string, Record<string, string>>; // All sections' colors
  masterSensorColorMap?: Record<string, string>; // Global sensor name -> color mapping for consistency across all data layers
  isSensorLoading?: (sectionId: string, sensorName: string) => boolean; // Check if sensor is loading

  // Display mode support
  hideManagedGraphsDropdown?: boolean; // Hide "Manage Graphs" dropdown (for display modes)

  // Feature toggles (for DataLayerDashboardContainer)
  enableCompareTable?: boolean;
  enableMarkers?: boolean;
  enableHarmonics?: boolean;
  enableSideBands?: boolean;
  enableHarmonicEdit?: boolean; // Show edit option in harmonics menu
  enableSideBandsEdit?: boolean; // Show edit option in carrier menu
  markerLabel?: string; // Custom label for marker button (e.g., "Timestamps" for sensors, "Markers" for spectrum)
  expandableLegendLabel?: string; // Custom label for expandable legend series (e.g., "Timestamps" for spectrum)
  syncTools?: {
    markers?: boolean; // Sync marker button state across sections
    harmonics?: boolean; // Sync harmonics button state across sections
    sideBands?: boolean; // Sync side bands button state across sections
  };

  // Legend sync mode props
  legendSyncMode?: LegendSyncMode;
  onLegendModeToggle?: () => void;
  showLegendModeToggle?: boolean;
  focusedSensors?: Record<string, string>; // Map of sectionId -> focused sensor name
  onFocus?: (sensorName: string, sectionId?: string) => void;
  onToggleFocusMode?: (sectionId: string) => void; // Toggle focus mode for a specific section

  // Global loading sensors state - synchronized across all sections
  loadingSensors?: Set<string>;
  setLoadingSensor?: (sensorName: string) => void;
  clearLoadingSensor?: (sensorName: string) => void;

  // Synced tool selection states (external state from parent)
  markerSelectionModeByChart?: Record<string, boolean>;
  setMarkerSelectionModeByChart?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  harmonicSelectionModeByChart?: Record<string, boolean>;
  setHarmonicSelectionModeByChart?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  sideBandsSelectionModeByChart?: Record<string, boolean>;
  setSideBandsSelectionModeByChart?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  customLineModeByChart?: Record<string, string | null>;
  setCustomLineModeByChart?: React.Dispatch<React.SetStateAction<Record<string, string | null>>>;

  // Mutual exclusivity for chip filters
  mutuallyExclusiveChipFilters?: string[];

  // Selected marker timestamp for highlighting
  selectedMarkerTimestamp?: string | null;

  // Selected timestamps set for multi-select highlighting
  selectedTimestampsSet?: Set<string>;

  // Expandable legend series (for timestamp rows beneath sensors)
  expandableLegendSeries?: any[];
  // Popover timestamps (flat list for snapshots dropdown)
  popoverTimestamps?: any[];
  onToggleExpandableSeries?: (seriesId: string) => void;
  onToggleExpandableSeriesFocus?: (seriesId: string) => void;
  onToggleExpandableTimestamp?: (seriesId: string, timestampId: string) => void;
  onRemoveMarker?: (markerId: string) => void; // Delete marker callback for popover

  // Focus mode for expandable legend series (timestamp multi-select)
  expandableLegendFocusMode?: boolean;

  // Enabled series for filtering which traces to show (e.g., acceleration/velocity in waveform)
  enabledSeries?: Set<string>;

  // Floating toolbar
  showFloatingToolbar?: boolean; // Show floating toolbar over charts instead of top toolbar

  // Inline focus button visibility
  showInlineFocusButton?: boolean; // Control visibility of inline "Focus" button next to each sensor (default: true)

  //Should show graphs in tab view
  isTabView?: boolean;

  showSectionControls?: boolean; // Show or hide the Clear/Reset buttons

  isExpandable?: boolean; // Enables expandable behavior (toggle button vs static title)
  // Expandable legend for API body (sent with data requests, not affecting signature)
  expandableLegendForApi?: any;
  // Drawing tools
  enableDrawing?: boolean; // Enable freehand drawing tools on charts
  // Y-Axis scale
  showYAxisScale?: boolean; // Show Y-axis scale selector
  enableDynamicMargin?: boolean; // Calculate dynamic margins based on data
  // Autoscale
  autoscaleMode?: 'none' | 'onload' | 'onFirstDataLoad' | 'onDataLoad';
  autoscaleAxis?: 'x' | 'y' | 'both';
  // Callback when section data changes (for audio integration in Waveform)
  onDataChange?: (sectionId: string, sectionData: any) => void;
}

/**
 * Generalized component for rendering managed graph sections
 * Handles graph selection, reset functionality, and dynamic dashboard rendering
 */
export const ManagedGraphsSection: React.FC<ManagedGraphsSectionProps> = ({
  title,
  dataLayer,
  graphs,
  selectedSections,
  onSelectionChange,
  syncSectionLegends,
  showReset = true,
  triggerReset = false,
  onReset,
  onResetDataLayer,
  loadingState = false,
  queryParams,
  forceRefresh,
  onLoadingStateChange,
  includeAlarms = false,
  variant = 'lines',
  customTitle,
  headerActions,
  chartFooterActions,
  cardClassName = 'flex flex-col w-full',
  contentClassName = 'flex flex-col w-full relative p-0 bg-transparent',
  verticalLinesState,
  benchmarkLineBySection,
  setBenchmarkLineBySection,
  syncMarking = true,
  filters,
  onFilterChange,
  externalLegendStates,
  onLegendToggle,
  onToggleSensor,
  onAddSensor,
  registerSensorToggleHandler,
  registerSensorData,
  sectionEnabledSensors,
  sectionSensorColors,
  masterSensorColorMap,
  isSensorLoading,
  hideManagedGraphsDropdown = false,
  enableCompareTable = false,
  enableMarkers = false,
  enableHarmonics = false,
  enableSideBands = false,
  enableDrawing = false,
  enableHarmonicEdit = true,
  enableSideBandsEdit = true,
  markerLabel = 'Markers',
  expandableLegendLabel,
  syncTools = {},
  legendSyncMode = 'multi',
  onLegendModeToggle,
  showLegendModeToggle = false,
  focusedSensors = {},
  onFocus,
  onToggleFocusMode,
  loadingSensors,
  setLoadingSensor,
  clearLoadingSensor,
  markerSelectionModeByChart,
  setMarkerSelectionModeByChart,
  harmonicSelectionModeByChart,
  setHarmonicSelectionModeByChart,
  sideBandsSelectionModeByChart,
  setSideBandsSelectionModeByChart,
  customLineModeByChart,
  setCustomLineModeByChart,
  mutuallyExclusiveChipFilters = [],
  selectedMarkerTimestamp,
  selectedTimestampsSet,
  expandableLegendSeries = [],
  popoverTimestamps,
  onToggleExpandableSeries,
  onToggleExpandableSeriesFocus,
  onToggleExpandableTimestamp,
  onRemoveMarker,
  expandableLegendFocusMode = false,
  enabledSeries,
  showFloatingToolbar = false,
  showInlineFocusButton = true,
  isTabView = false,
  showSectionControls,
  isExpandable,
  expandableLegendForApi,
  showYAxisScale = false,
  enableDynamicMargin = false,
  autoscaleMode = 'none',
  autoscaleAxis = 'both',
  onDataChange,
}) => {
  // Stabilize graphs array reference
  const stableGraphs = useMemo(() => graphs, [graphs]);
  const [activeDataLayerFilters, setActiveDataLayerFilters] = useState<any>(filters);

  const prevIsTabView = useRef(isTabView);

  // Sync chip filters from props (for spectrum timestamp updates) while preserving non-chip filters (for sensor search)
  useEffect(() => {
    if (!filters) return;

    setActiveDataLayerFilters((prev: any) => {
      if (!prev || prev.length === 0) return filters;

      // Update only chip filters from props, keep other filters from state
      return prev.map((stateFilter: any) => {
        const propFilter = filters.find((f: any) => f.id === stateFilter.id);

        // If it's a chip filter, use the updated one from props (has correct enabled states from config)
        if (propFilter && propFilter.type === 'chips') {
          return propFilter;
        }

        // Otherwise keep the state version (has user's manual changes)
        return stateFilter;
      });
    });
  }, [filters]);

  // Generate graph options for the dropdown
  const graphOptions = useMemo(() => {
    return stableGraphs.map((graph: any) => ({
      id: graph.id,
      label: toLabel(graph.title),
      ...(graph.shortTitle ? { shortLabel: toLabel(graph.shortTitle) } : {}),
      checked: selectedSections.includes(graph.id),
    }));
  }, [stableGraphs, selectedSections]);

  // Generate enabled graphs mapping
  const enabledGraphs = useMemo(
    () => Object.fromEntries(stableGraphs.map((el: any) => [el.id, selectedSections.includes(el.id)])),
    [stableGraphs, selectedSections]
  );

  // Handle selection change from dropdown
  const handleSelectionChange = useCallback(
    (selectedGroups: Record<string, CheckboxOption[]>) => {
      const selected = Object.values(selectedGroups)
        .flat()
        .map((el) => el.id);

      // Only update if there's an actual change
      const isSame =
        selected.length === selectedSections.length && selected.every((id) => selectedSections.includes(id));

      if (!isSame) {
        onSelectionChange(selected);
      }
    },
    [selectedSections, onSelectionChange]
  );

  // Enhanced query params with data layer
  const enhancedQueryParams = useMemo(
    () => ({
      ...queryParams,
      data_layer: dataLayer,
    }),
    [queryParams, dataLayer]
  );

  // Memoize the groups array for CheckboxDropdown to prevent infinite re-renders
  const dropdownGroups = useMemo(
    () => [
      {
        title: ' ',
        requireSelection: true,
        options: graphOptions,
      },
    ],
    [graphOptions]
  );

  useEffect(() => {
    // ONLY when going from Tab → Dropdown
    if (prevIsTabView.current && !isTabView) {
      const allIds = dropdownGroups.flatMap((group) => group.options).map((option) => option.id);

      const isSame = allIds.length === selectedSections.length && allIds.every((id) => selectedSections.includes(id));

      if (!isSame) {
        onSelectionChange(allIds);
      }
    }

    prevIsTabView.current = isTabView;
  }, [isTabView, dropdownGroups, selectedSections, onSelectionChange]);

  // Memoize the entire dropdown to prevent re-renders
  const dropdownComponent = useMemo(
    () =>
      isTabView ? (
        <TabSelector
          groups={dropdownGroups}
          onSelectionChange={handleSelectionChange}
          dataTestid="graph_control_manage_graphs"
        />
      ) : (
        <CheckboxDropdown
          placementAlign="end"
          triggerVariant="ghost"
          triggerClassName="py-0"
          groups={dropdownGroups}
          triggerText={<span className="flex items-center text-sm font-semibold gap-2">Manage Graphs</span>}
          onSelectionChange={handleSelectionChange}
          dataTestid="graph_control_manage_graphs"
        />
      ),
    [dropdownGroups, handleSelectionChange, isTabView]
  );

  const updateFilter = (filters: DataLayerFilter[], filterId: string, value: string | string[]) => {
    return filters.map((filter) => {
      if (filter.id === filterId) {
        // Check if THIS filter is configured for multi-select
        const isMultiSelect = filter.isMultiSelect;
        const values = Array.isArray(value) ? value : [value];
        const selectedSet = new Set(values);

        if (isMultiSelect) {
          return {
            ...filter,
            // defaultValue must be a string (not array) for API compatibility
            defaultValue: values[0] || '',
            options: filter.options.map((option) => ({
              ...option,
              enabled: selectedSet.has(option.value),
            })),
          };
        } else {
          const normalizedValue = values[0];
          return {
            ...filter,
            defaultValue: normalizedValue,
            options: filter.options.map((option) => ({
              ...option,
              enabled: option.value === normalizedValue,
            })),
          };
        }
      }
      return filter;
    });
  };

  return (
    <BaseCard
      variant="wrapper"
      classNames={{
        card: cardClassName,
        content: contentClassName,
      }}
      title={
        customTitle || (
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-2" data-testid={`sensors_graph_title_${generateTestId(title)}`}>
              {!isTabView ? <CardTitle text={title} /> : !hideManagedGraphsDropdown && dropdownComponent}
            </div>
            <div className="flex items-center gap-2">
              {filters && filters.length > 0 && (
                <>
                  {/* Render group filters separately */}
                  {filters
                    .filter((f) => f && f.type === 'group')
                    .map((filter) => {
                      return (
                        <GroupDataLayerFilters
                          key={filter.id}
                          filter={filter as any}
                          onFilterChange={(groupId, values) => {
                            // The filter's own handler should be called from within GroupDataLayerFilters
                            // This onFilterChange is just for backward compatibility
                            if (onFilterChange) {
                              if (typeof values === 'object' && !Array.isArray(values)) {
                                // It's a Record<string, string[]>, we need to handle it specially
                                // For group filters, we typically care about all selections
                                const allSelections = Object.values(values).flat();
                                onFilterChange(groupId, allSelections);
                              } else {
                                // It's already string[]
                                onFilterChange(groupId, values as string[]);
                              }
                            }
                          }}
                        />
                      );
                    })}
                  {/* Render all other filter types (chips, dropdown, search) */}
                  {filters.filter((f) => f && f.type !== 'group').length > 0 &&
                    (() => {
                      // Combine all enabled sensors and colors from all sections (like comparison table)
                      const combinedEnabledSensors: Record<string, boolean> = {};
                      const combinedSensorColors: Record<string, string> = {};

                      Object.values(sectionEnabledSensors || {}).forEach((sectionSensors) => {
                        Object.assign(combinedEnabledSensors, sectionSensors);
                      });

                      Object.values(sectionSensorColors || {}).forEach((sectionColors) => {
                        Object.assign(combinedSensorColors, sectionColors);
                      });

                      // Create toggle handler that directly calls the registered handler for this data layer
                      // Both toggle and add use the same logic now since the container handles toggle internally
                      const handleToggleSensorForSearch = (sectionId: string, sensorName: string) => {
                        // With sectionId provided, we can directly target the right graph
                        const targetGraph = graphs.find((g: any) => g.id === sectionId);

                        if (!targetGraph) {
                          return;
                        }

                        // Enable the graph if it's not already selected
                        if (!selectedSections.includes(sectionId)) {
                          const newSections = [...selectedSections, sectionId];
                          onSelectionChange(newSections);
                        }

                        // Call the toggle handler (prioritize onToggleSensor, fallback to onAddSensor)
                        const handler = onToggleSensor || onAddSensor;
                        if (handler) {
                          handler(sectionId, sensorName);
                        } else {
                        }
                      };

                      // Both handlers use the same logic
                      const handleAddSensorForSearch = handleToggleSensorForSearch;

                      // Create loading sensors set from actual loading state
                      const loadingSensorsSet = new Set<string>();
                      // Check if any sensors are loading using the isSensorLoading function
                      if (isSensorLoading) {
                        Object.keys(combinedEnabledSensors).forEach((sensorName) => {
                          // Check if this sensor is loading in any of the sections
                          for (const sectionId of Object.keys(sectionEnabledSensors || {})) {
                            if (isSensorLoading(sectionId, sensorName)) {
                              loadingSensorsSet.add(sensorName);
                              break; // Once we know it's loading, no need to check other sections
                            }
                          }
                        });
                      }

                      return (
                        <DataLayerFilters
                          filters={filters.filter((f) => f && f.type !== 'group')}
                          onFilterChange={(filterId, value: string | string[]) => {
                            const updatedDataLayerFilters = updateFilter([...filters], filterId, value);
                            setActiveDataLayerFilters(updatedDataLayerFilters);

                            if (onFilterChange) {
                              onFilterChange(filterId, value);
                            }
                          }}
                          enabledSensors={combinedEnabledSensors}
                          onToggleSensor={handleToggleSensorForSearch}
                          onAddSensor={handleAddSensorForSearch}
                          sensorColors={combinedSensorColors}
                          loadingSensors={loadingSensorsSet}
                          syncSectionLegends={syncSectionLegends}
                          // Legend sync mode props
                          legendSyncMode={legendSyncMode}
                          onLegendModeToggle={onLegendModeToggle}
                          showLegendModeToggle={showLegendModeToggle}
                          onFocus={onFocus}
                          // Mutual exclusivity for chip filters
                          mutuallyExclusiveChipFilters={mutuallyExclusiveChipFilters}
                        />
                      );
                    })()}
                </>
              )}
              {(() => {
                return null;
              })()}
              {/* Additional header actions (e.g., playback controls) */}
              {headerActions}
              {showReset && onReset && (
                <Button
                  className="flex items-center text-sm font-semibold gap-2 py-1"
                  variant="ghost"
                  onClick={() => {
                    onReset();
                  }}
                  disabled={loadingState}
                  data-testid="graph_control_reset_to_default"
                >
                  <RefreshCw />
                  Reset to Default
                </Button>
              )}
              {!isTabView && !hideManagedGraphsDropdown && dropdownComponent}
            </div>
          </div>
        )
      }
    >
      <DataLayerDashboardContainer
        onResetDataLayer={
          onResetDataLayer ||
          (() => {
            // Default no-op reset handler
          })
        }
        sectionConfigGraphs={stableGraphs}
        enabledGraphs={enabledGraphs}
        triggerReset={triggerReset}
        activeDataLayerFilters={activeDataLayerFilters}
        queryParams={enhancedQueryParams}
        onLoadingStateChange={onLoadingStateChange}
        forceRefresh={forceRefresh}
        includeAlarms={includeAlarms}
        variant={variant}
        verticalLinesState={syncMarking ? verticalLinesState : undefined}
        benchmarkLineBySection={benchmarkLineBySection}
        setBenchmarkLineBySection={setBenchmarkLineBySection}
        externalLegendStates={externalLegendStates}
        onExternalLegendToggle={onLegendToggle}
        onToggleSensor={onToggleSensor}
        registerSensorToggleHandler={registerSensorToggleHandler}
        registerSensorData={registerSensorData}
        masterSensorColorMap={masterSensorColorMap}
        dataLayer={dataLayer}
        syncSectionLegends={syncSectionLegends}
        enableCompareTable={enableCompareTable}
        enableMarkers={enableMarkers}
        enableHarmonics={enableHarmonics}
        enableSideBands={enableSideBands}
        enableDrawing={enableDrawing}
        enableHarmonicEdit={enableHarmonicEdit}
        enableSideBandsEdit={enableSideBandsEdit}
        markerLabel={markerLabel}
        expandableLegendLabel={expandableLegendLabel}
        syncTools={syncTools}
        focusedSensors={focusedSensors}
        focusMode={legendSyncMode === 'focus'}
        onFocus={onFocus}
        onToggleFocusMode={onToggleFocusMode}
        loadingSensors={loadingSensors}
        setLoadingSensor={setLoadingSensor}
        clearLoadingSensor={clearLoadingSensor}
        markerSelectionModeByChart={markerSelectionModeByChart}
        setMarkerSelectionModeByChart={setMarkerSelectionModeByChart}
        harmonicSelectionModeByChart={harmonicSelectionModeByChart}
        setHarmonicSelectionModeByChart={setHarmonicSelectionModeByChart}
        sideBandsSelectionModeByChart={sideBandsSelectionModeByChart}
        setSideBandsSelectionModeByChart={setSideBandsSelectionModeByChart}
        customLineModeByChart={customLineModeByChart}
        setCustomLineModeByChart={setCustomLineModeByChart}
        selectedMarkerTimestamp={selectedMarkerTimestamp}
        selectedTimestampsSet={selectedTimestampsSet}
        expandableLegendSeries={expandableLegendSeries}
        expandableLegendFocusMode={expandableLegendFocusMode}
        enabledSeries={enabledSeries}
        popoverTimestamps={popoverTimestamps}
        onToggleExpandableSeries={onToggleExpandableSeries}
        onToggleExpandableSeriesFocus={onToggleExpandableSeriesFocus}
        onToggleExpandableTimestamp={onToggleExpandableTimestamp}
        onRemoveMarker={onRemoveMarker}
        showFloatingToolbar={showFloatingToolbar}
        showInlineFocusButton={showInlineFocusButton}
        showSectionControls={showSectionControls}
        isExpandable={isExpandable}
        isTimestampsLoading={loadingState}
        expandableLegendForApi={expandableLegendForApi}
        showYAxisScale={showYAxisScale}
        enableDynamicMargin={enableDynamicMargin}
        autoscaleMode={autoscaleMode}
        autoscaleAxis={autoscaleAxis}
        onDataChange={onDataChange}
      />
      {/* Actions below the chart area (e.g., playback controls) */}
      {chartFooterActions}
    </BaseCard>
  );
};

function TabSelector({
  groups,
  requireSelection = false,
  dataTestid,
  onChange,
  onSelectionChange,
}: CheckboxDropdownProps) {
  const { options, handleCheckedChange } = useGroupedSelection({
    groups,
    requireSelection,
    onChange,
    onSelectionChange,
    selectionMode: 'single',
  });

  const { visualAIPlaylistItems, visualAIActiveItemIndex } = useSelector((state: RootState) => state.assetsStudio);
  const selectedEvidence = visualAIPlaylistItems?.[visualAIActiveItemIndex];
  const prevBeltPosition = useRef<string | null>(null);

  useEffect(() => {
    const beltPosition = selectedEvidence?.belt_position;
    if (!beltPosition) return;

    // Prevent feedback loop
    if (prevBeltPosition.current === beltPosition) return;

    prevBeltPosition.current = beltPosition;

    const tabId = beltPosition === 'right' ? 'rightRoller' : 'leftRoller';
    handleCheckedChange(tabId, true);
  }, [selectedEvidence?.belt_position]);

  return (
    <div className="flex gap-2">
      {groups[0].options.map((opt) => {
        const checked = options[opt.id]?.checked;

        return (
          <Button
            key={opt.id}
            size="sm"
            variant="default"
            onClick={() => handleCheckedChange(opt.id, true)}
            className={cn('font-semibold', checked ? 'bg-[#01152B1F] text-dark' : '')}
            {...(dataTestid ? { 'data-testid': dataTestid } : {})}
          >
            {opt.shortLabel ?? opt.label}
          </Button>
        );
      })}
    </div>
  );
}
